import React, { useEffect, useMemo, useState } from "react";
import casePaidTaskStyle from "./casePaidTaskStyle";
import DeletePaidTaskModal from "../PaidTask/PaidTaskView/DeletePaidTaskModal";
import PaidTaskViewHeader from "../PaidTask/PaidTaskView/PaidTaskViewHeader";
import Grid from "@mui/material/Grid";
import { convertFromFloat, convertToFloat, formValue } from "../../services/formServiceFunctions";
import { getTemplates, updateFeeValidators } from "../../services/paidTaskService";
import InternalPaidTaskDefinitionButton from "../PaidTask/PaidTaskView/InternalPaidTaskDefinitionButton";
import ValidatorForm from "../Validator/ValidatorForm";
import useForm from "../../hooks/useForm";
import _ from "lodash";
import { Box, MenuItem } from "@mui/material";
import ValidatorSelect from "../Validator/ValidatorSelect";
import PaidTaskFormFields from "./PaidTaskFormFields";
import { PAID_TASK_STATUS_CREATED, PAID_TASK_STATUS_REQUESTED } from "../PaidTask/paidTaskStatus";
import ContentBox from "../ContentBox/ContentBox";
import PaidTaskLog from "./PaidTaskLog";
import { useMutation } from "@tanstack/react-query";
import { updateResource } from "../../services/ReactQuery/reactQueryService";
import { useCurrentUser } from "../../provider/CurrentUserProvider";
import { PaidTask } from "../../types/PaidTask";
import { listViolations } from "../../services/formatterService";
import { useSnackbar } from "notistack";
import { ProductUnion } from "../../types/ProductUnion";
import dayjs from "dayjs";

type CasePaidTaskDefinitionProps = {
  paidTask: PaidTask;
  product: ProductUnion;
};

const CasePaidTaskDefinition = ({ paidTask, product }: CasePaidTaskDefinitionProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const currentUser = useCurrentUser();
  const updateMutation = useMutation({
    mutationFn: updateResource
  });
  const { enqueueSnackbar } = useSnackbar();

  const initialValues = {
    subject: paidTask.subject,
    description: paidTask.description,
    definitionOfDone: paidTask.definitionOfDone,
    reviewRemarks: paidTask.reviewRemarks,
    assignmentDeadline: dayjs(paidTask.assignmentDeadline),
    doneDeadline: dayjs(paidTask.doneDeadline),
    fee: convertFromFloat(paidTask.fee),
    type: paidTask.type,
  };

  const onSubmit = async () => {
    setIsLoading(true);
    try {
      await updateMutation.mutateAsync({
        id: paidTask.id,
        uri: "paid_tasks",
        data: {
          subject: formValue(values, "subject"),
          description: formValue(values, "description"),
          definitionOfDone: formValue(values, "definitionOfDone"),
          reviewRemarks: formValue(values, "reviewRemarks"),
          assignmentDeadline: formValue(values, "assignmentDeadline"),
          doneDeadline: formValue(values, "doneDeadline"),
          fee: convertToFloat(formValue(values, "fee")),
        },
      });
    } catch (e: any) {
      enqueueSnackbar("Aufgabe konnte nicht bearbeitet werden: " + (e.errors ? listViolations(e.errors) : ""), {
        variant: "error",
        persist: true,
        isNonInteractive: true,
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "center",
        },
      });
    }
    setIsLoading(false);
  };

  const {
    values,
    errors,
    handleChange,
    handleSubmit,
    registerValidators,
    handleBlur,
    handleDateChange,
    touchedValues,
  } = useForm({ initialValues, onSubmit });

  const templates = useMemo(() => {
    return getTemplates(product);
  }, [product]);

  useEffect(() => {
    updateFeeValidators(registerValidators, values.type, product);
  }, [values.type]);

  return (
    <ContentBox headline="Aufgabe" headlineVariant="h2">
      <Box sx={casePaidTaskStyle.verticalSpace} />
      <ValidatorForm onSubmit={handleSubmit}>
        <PaidTaskViewHeader paidTask={paidTask} />
        <Grid container spacing={2} sx={{
          alignItems: "flex-start"
        }}>
          <Grid
            size={{
              xs: 12,
              md: 6
            }}>
            <ValidatorSelect
              label={"Typ"}
              name={"type"}
              value={formValue(values, "type")}
              disabled
              onChange={handleChange}
            >
              {_.map(templates, (template) => {
                return (
                  <MenuItem key={template.type} value={template.type}>
                    {template.label}
                  </MenuItem>
                );
              })}
            </ValidatorSelect>
          </Grid>
          <PaidTaskFormFields
            registerValidators={registerValidators}
            values={values}
            errors={errors}
            handleBlur={handleBlur}
            handleChange={handleChange}
            handleDateChange={handleDateChange}
            isDisabled={
              currentUser.isExternal ||
              !_.includes([PAID_TASK_STATUS_CREATED, PAID_TASK_STATUS_REQUESTED], paidTask.status)
            }
          />
        </Grid>
        <Grid size={12}>
          <Box sx={casePaidTaskStyle.verticalSpace} />
        </Grid>
        <Grid sx={casePaidTaskStyle.buttonArea} size={12}>
          {!currentUser.isExternal && (
            <>
              <InternalPaidTaskDefinitionButton
                paidTask={paidTask}
                isLoading={isLoading}
                touchedValues={touchedValues}
                openDeleteModal={() => setConfirmDeleteOpen(true)}
              />
              <PaidTaskLog paidTask={paidTask} />
            </>
          )}
        </Grid>
      </ValidatorForm>
      <DeletePaidTaskModal
        paidTask={paidTask}
        isLoading={isLoading}
        setIsLoading={setIsLoading}
        handleClose={() => setConfirmDeleteOpen(false)}
        open={confirmDeleteOpen}
      />
    </ContentBox>
  );
};

export default CasePaidTaskDefinition;
